/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useMemo, useState } from "react";
import { Layout } from "../../../../ui/components/Layout/Layout";
import { TViewComponent } from "../../../../view/IView";
import { ViewEditorView } from "../ViewEditorView";
import { ViewEditorComponentPalette } from "./ViewEditorComponentPalette";
import { ViewEditorInspector } from "./ViewEditorInspector";
import { ViewEditorMainToolbar } from "./ViewEditorMainToolbar";
import { ViewEditorConsoleContent } from "./ViewEditorConsole";
import { DOC_ERROR_SEVERITY } from "@adapptio/lib-blueprint";
import { ViewEditorDataExplorerContent, ViewEditorDataExplorerToolbar } from "./ViewEditorDataExplorer";
import { ViewEditorOutlineContent } from "./ViewEditorOutline";
import { ViewEditorSettings } from "./ViewEditorSettings";
import { ModelRuntimeContextProvider } from "../../../../ui/contexts/ModelRuntimeContext";
import { TLayoutState } from "../../../../ui/components/Layout/LayoutState";
import { ViewEditorOutlineToolbar } from "./ViewEditorOutlineToolbar";
import { ViewEditorMainContent } from "./ViewEditorMainContent";
import { ViewEditorEventFlowContent, ViewEditorEventFlowToolbar } from "./ViewEditorEventFlow";
import { ViewEditorSecondaryToolbar } from "./ViewEditorSecondaryToolbar";
import { ViewEditorTesterContent } from "./ViewEditorTester";
import { ViewEditorConsoleFilters } from "./ViewEditorConsoleFilters";
import { ConsoleCountBadge } from "../../../../ui/components/ConsoleCountBadge";
import {
	defaultPreviewResolutionSettings,
	PREVIEW_RESOLUTION_SETTINGS_STORAGE_KEY,
	PreviewResolutionProvider
} from "../../../../previewResolution";
import { useEditorState } from "../../../../ui/hooks";
import { ReferenceListPanel } from "../../../../ui/components/ReferenceList/ReferenceListPanel";
import { ReferenceCountBadge } from "../../../../ui/components/ReferenceList/ReferenceCountBadge";

export const ViewEditorEditMain: TViewComponent<ViewEditorView> = ({ view }) => {
	const [ severityFilter, setSeverityFilter ] = useState<Set<DOC_ERROR_SEVERITY>>(new Set());
	const viewModel = useMemo(() => view.getViewModel(), [ view ]);
	const layoutState = useMemo(() => view.getLayout(), []);

	const [ previewResolutionSettings, savePreviewResolutionSettings ] = useEditorState(
		PREVIEW_RESOLUTION_SETTINGS_STORAGE_KEY,
		defaultPreviewResolutionSettings
	);

	const handleFilterSelect = useCallback<(severity: DOC_ERROR_SEVERITY) => void>((severity) => {
		setSeverityFilter((prevState) => {
			prevState.delete(severity) || prevState.add(severity);
			return new Set(prevState);
		});
	}, []);

	const handleActivate = useCallback(
		(element: TLayoutState) => {
			switch (element.id) {
				case "outline":
				case "dataExplorer":
				case "content":
				case "eventFlow":
				case "panelRight": {
					view.setActiveLayoutPanel(element.id);
					break;
				}
			}
		},
		[ view ]
	);

	const renderComponent = (componentName: string) => {
		switch (componentName) {
			case "outlineToolbar": {
				return <ViewEditorOutlineToolbar model={viewModel} />;
			}
			case "outlineContent": {
				return <ViewEditorOutlineContent model={viewModel} />;
			}

			case "dataExplorerToolbar": {
				return <ViewEditorDataExplorerToolbar />;
			}
			case "dataExplorerContent": {
				return <ViewEditorDataExplorerContent model={viewModel} />;
			}

			case "mainToolbar": {
				return <ViewEditorMainToolbar view={view} />;
			}
			case "secondaryMainToolbar": {
				return <ViewEditorSecondaryToolbar view={view} />;
			}
			case "mainContent": {
				return <ViewEditorMainContent view={view} />;
			}

			case "eventFlowToolbar": {
				return <ViewEditorEventFlowToolbar view={view} />;
			}
			case "eventFlowContent": {
				return <ViewEditorEventFlowContent view={view} />;
			}

			case "tester": {
				return <ViewEditorTesterContent view={view} />;
			}

			case "console": {
				return <ViewEditorConsoleContent model={viewModel} severityFilter={severityFilter} />;
			}

			case "consoleFilters": {
				return (
					<ViewEditorConsoleFilters
						model={viewModel}
						severityFilter={severityFilter}
						onFilterSelect={handleFilterSelect}
					/>
				);
			}

			case "consoleCount": {
				return <ConsoleCountBadge model={viewModel} />;
			}

			case "references": {
				return <ReferenceListPanel
					resourceId={view.getResource().id}
					dCtx={viewModel.getDesignContext()}
					resourceService={view.getResourceService()}
				/>;
			}

			case "referencesCount": {
				return <ReferenceCountBadge
					resourceId={view.getResource().id}
					dCtx={viewModel.getDesignContext()}
					resourceService={view.getResourceService()}
				/>;
			}

			case "componentPalette": {
				return <ViewEditorComponentPalette model={viewModel} />;
			}
			case "inspector": {
				return <ViewEditorInspector model={viewModel} />;
			}
			case "settings": {
				return <ViewEditorSettings model={viewModel} />;
			}

			default: {
				return <p>Error: Undefined panel {componentName}.</p>;
			}
		}
	};

	return (
		<PreviewResolutionProvider
			previewResolutionSettings={previewResolutionSettings}
			onPreviewResolutionSettingsChange={savePreviewResolutionSettings}
		>
			<ModelRuntimeContextProvider rCtx={viewModel.getRuntimeContext()}>
				<Layout state={layoutState} renderComponent={renderComponent} onActivate={handleActivate} />
			</ModelRuntimeContextProvider>
		</PreviewResolutionProvider>
	);
};
